import React, { useState } from 'react';
import { Wand2, Loader2, Layers } from 'lucide-react';
import { Level, LevelType } from '../../types/Level';
import { LEVEL_TYPE_LABELS, ORDERED_TYPES } from '../../studio/planner';
import { MAX_BATCH } from '../../studio/pipeline';
import { typeStats, isStale, TypeStat } from '../../studio/typeStats';
import { DEFAULT_FILL } from '../../studio/gaps';
import { estimateCost, formatUsd } from '../../studio/cost';

const sinceLastText = (stat: TypeStat): string => {
  if (stat.sinceLast === null) return 'never used';
  if (stat.sinceLast === 0) return 'used in the newest level';
  return `${stat.sinceLast} level${stat.sinceLast === 1 ? '' : 's'} since last use`;
};

interface OrderPanelProps {
  levels: Level[];
  counts: Partial<Record<LevelType, number>>;
  theme: string;
  running: boolean;
  hasClaudeKey: boolean;
  // Without an image key, songs and sayings are not drawn automatically
  hasGeminiKey: boolean;
  withFacts: boolean;
  onCount: (type: LevelType, count: number) => void;
  onTheme: (theme: string) => void;
  onWithFacts: (value: boolean) => void;
  // Sets the counts for this many levels, favoring the types that have been missing the longest
  onFill: (total: number) => void;
  onStart: () => void;
}

const OrderPanel: React.FC<OrderPanelProps> = ({
  levels, counts, theme, running, hasClaudeKey, hasGeminiKey, withFacts, onCount, onTheme, onWithFacts, onFill, onStart,
}) => {
  // Kept as typed, so the box can be cleared and retyped; it is turned into a number when the button is clicked
  const [fillText, setFillText] = useState(String(DEFAULT_FILL));
  const fillSize = fillText.trim() === '' || !isFinite(Number(fillText))
    ? DEFAULT_FILL
    : Math.min(MAX_BATCH, Math.max(1, Math.floor(Number(fillText))));
  const stats = typeStats(levels);
  const total = ORDERED_TYPES.reduce((sum, type) => sum + (counts[type] || 0), 0);
  const overLimit = total > MAX_BATCH;
  const canStart = hasClaudeKey && !running && total > 0 && !overLimit;
  const cost = estimateCost(counts, { facts: withFacts, pictures: hasGeminiKey });
  const drawnTypes = (counts[LevelType.WhatsTheSong] || 0) + (counts[LevelType.KnownSentence] || 0);

  return (
    <div className="studio-panel">
      <h3>New batch</h3>
      <p className="studio-hint" style={{ margin: '0 0 0.75rem' }}>
        Each type shows how many levels it has and how long ago it last appeared. Types in red have been missing for 5 levels or more.
      </p>

      <div className="studio-fill">
        <button className="btn btn-secondary btn-small" onClick={() => onFill(fillSize)} disabled={running}>
          <Layers size={14} /> Fill the gaps
        </button>
        <span>with</span>
        <input
          type="number"
          aria-label="Levels to fill"
          min={1}
          max={MAX_BATCH}
          value={fillText}
          disabled={running}
          onChange={(e) => setFillText(e.target.value)}
        />
        <span>levels. Chooses the types that have been missing the longest, counting what is already in the queue.</span>
      </div>

      <div className="studio-counts">
        {ORDERED_TYPES.map(type => (
          <label key={type} className={`studio-count${isStale(stats[type]) ? ' stale' : ''}`}>
            {LEVEL_TYPE_LABELS[type]}
            <span className="studio-count-stat">
              {stats[type].count} level{stats[type].count === 1 ? '' : 's'} · {sinceLastText(stats[type])}
            </span>
            <input
              type="number"
              min={0}
              max={MAX_BATCH}
              value={counts[type] || 0}
              disabled={running}
              onChange={(e) => onCount(type, Math.max(0, Math.floor(Number(e.target.value) || 0)))}
            />
          </label>
        ))}
      </div>

      <label className="studio-theme">
        Theme (optional)
        <input
          type="text"
          value={theme}
          disabled={running}
          placeholder="למשל: חנוכה, שירי שנות ה-90"
          onChange={(e) => onTheme(e.target.value)}
        />
      </label>

      <label className="studio-check">
        <input type="checkbox" checked={withFacts} disabled={running} onChange={(e) => onWithFacts(e.target.checked)} />
        Write a fun fact for every level and check it on the web
      </label>

      {total > 0 && (
        <p className="studio-hint studio-cost">
          Estimated cost: about {formatUsd(cost.total)} (Claude {formatUsd(cost.claude)}{cost.gemini > 0 ? `, Gemini ${formatUsd(cost.gemini)}` : ''}).
          {' '}A rough figure: a redrawn picture or a long web search adds to it.
          {!hasGeminiKey && drawnTypes > 0 ? ' Pictures are not included, because no Gemini key is saved.' : ''}
        </p>
      )}

      <div className="studio-start">
        <button className="btn btn-primary" onClick={onStart} disabled={!canStart}>
          {running
            ? <><Loader2 className="animate-spin" size={16} /> Planning {total} levels...</>
            : <><Wand2 size={16} /> Generate {total > 0 ? total : ''} levels</>}
        </button>
        {!hasClaudeKey && (
          <span className="studio-hint">Add a Claude API key in Settings &gt; API Keys to use the Level Studio.</span>
        )}
        {overLimit && <span className="studio-hint">A batch is limited to {MAX_BATCH} levels.</span>}
      </div>
    </div>
  );
};

export default OrderPanel;
